// NoVA Welle A — deterministic vehicle-import breakdown card.
// Backend contract: `vehicle_import_breakdown` (nova_engine.build_import_widget).
// The frontend ONLY renders — no calculation logic lives here.
import React from "react";
import { useTranslation } from "react-i18next";
import { Calculator, AlertTriangle } from "lucide-react";

function eur(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}

function Row({ label, value, accent, testid }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-3 text-sm" data-testid={testid}>
      <span className="text-zinc-500">{label}</span>
      <span className={`font-medium text-right ${accent || "text-zinc-100"}`}>
        {value}
      </span>
    </div>
  );
}

export function VehicleImportWidget({ widget }) {
  const { t } = useTranslation("ai");
  const tr = (k, opt) => t(`widgets.vehicleImport.${k}`, opt);
  const w = widget || {};
  if (w.status !== "ok") return null;

  const nova = w.nova || {};
  const range = w.nova_range;
  const market = w.at_market;
  const advantage = typeof w.advantage === "number" ? w.advantage : null;
  const warnings = (w.warnings || []).filter(
    (c) => c !== "registration_cost_not_included",
  );
  const confidence = w.confidence;

  return (
    <div
      data-testid="ai-widget-vehicle-import"
      className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-2 max-w-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wide min-w-0">
          <Calculator className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{tr("title")}</span>
        </div>
        {confidence && (
          <span
            data-testid="vehicle-import-confidence"
            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
              confidence === "high"
                ? "bg-emerald-500/15 text-emerald-300"
                : confidence === "medium"
                  ? "bg-amber-500/15 text-amber-300"
                  : "bg-zinc-700/50 text-zinc-300"
            }`}
          >
            {tr(`confidence.${confidence}`, { defaultValue: confidence })}
          </span>
        )}
      </div>

      <Row label={tr("offerPrice")} value={eur(w.offer_price)} testid="vehicle-import-offer-price" />
      {w.vat_code && (
        <div className="text-xs text-zinc-400" data-testid="vehicle-import-vat">
          {tr(`vatCodes.${w.vat_code}`, { defaultValue: w.vat_code })}
        </div>
      )}

      {w.calculable ? (
        <>
          {w.basis_net != null && w.basis_net !== w.offer_price && (
            <Row label={tr("basisNet")} value={eur(w.basis_net)} testid="vehicle-import-basis" />
          )}
          <Row
            label={`${tr("nova")}${nova.rate_pct != null ? ` (${nova.rate_pct} %)` : ""}`}
            value={eur(nova.total)}
            accent="text-amber-400"
            testid="vehicle-import-nova"
          />
          {range && (
            <div className="text-xs text-zinc-500" data-testid="vehicle-import-nova-range">
              {tr("novaRange")}: {eur(range.low)} – {eur(range.high)}
              {" · "}
              {tr("novaRangeBasis", { count: range.sample })}
            </div>
          )}
          {w.at_vat_due > 0 && (
            <Row label={tr("atVat")} value={eur(w.at_vat_due)} testid="vehicle-import-at-vat" />
          )}
          {w.transport_cost > 0 && (
            <Row label={tr("transport")} value={eur(w.transport_cost)} />
          )}
          <Row
            label={tr("registration")}
            value={w.registration_cost > 0 ? eur(w.registration_cost) : tr("registrationNotIncluded")}
            accent={w.registration_cost > 0 ? undefined : "text-zinc-500 text-xs"}
          />
          <div className="border-t border-zinc-800 pt-2">
            <Row
              label={tr("landedCost")}
              value={eur(w.landed_cost)}
              accent="text-lg font-semibold text-amber-400"
              testid="vehicle-import-landed-cost"
            />
          </div>
          {market && market.median != null ? (
            <>
              <Row
                label={`${tr("atMarket")} (${tr("sampleBasis", { count: market.sample })})`}
                value={eur(market.median)}
                testid="vehicle-import-market"
              />
              {advantage != null && (
                <Row
                  label={advantage >= 0 ? tr("advantage") : tr("disadvantage")}
                  value={`${advantage >= 0 ? "+" : ""}${eur(Math.abs(advantage)).replace("€ ", "€ ")}`}
                  accent={advantage >= 0 ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}
                  testid="vehicle-import-advantage"
                />
              )}
            </>
          ) : market ? (
            <div className="text-xs text-zinc-500">
              {tr("weakBasis", { count: market.sample || 0 })}
            </div>
          ) : null}
        </>
      ) : (
        <div className="space-y-1.5" data-testid="vehicle-import-missing">
          {w.is_tax_new && (
            <div className="flex gap-1.5 text-xs text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{tr("taxNew")}</span>
            </div>
          )}
          {(w.missing_fields || []).length > 0 && (
            <>
              <div className="text-xs text-zinc-400">{tr("notCalculable")}</div>
              <ul className="text-xs text-zinc-300 list-disc list-inside space-y-0.5">
                {w.missing_fields.map((f) => (
                  <li key={f}>{tr(`missing.${f}`, { defaultValue: f })}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="space-y-0.5 pt-1" data-testid="vehicle-import-warnings">
          {warnings.map((c) => (
            <div key={c} className="flex gap-1.5 text-[11px] text-zinc-500">
              <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5 text-amber-500/70" />
              <span>{tr(`warnings.${c}`, { defaultValue: c })}</span>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-zinc-800/70 pt-1.5 space-y-0.5">
        {w.rule?.formula && (
          <div className="text-[10px] text-zinc-600">
            {tr("legalBasis")}: {w.rule.rule_id} · {w.rule.formula}
          </div>
        )}
        <div className="text-[10px] text-zinc-600" data-testid="vehicle-import-disclaimer">
          {tr("disclaimer")}
        </div>
      </div>
    </div>
  );
}
